import { Bell, AlertTriangle, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNotificationSchedules } from "@/hooks/useNotificationSchedules";
import { useActiveOrganization } from "@/hooks/useActiveOrganization";
import { formatCurrency } from "@/utils/currencyFormatter";
import { Link } from "react-router-dom";
import { MessageCircle, CheckCircle, Calendar } from "lucide-react";
import { useState } from "react";
import PartialPaymentForm from "@/components/finance/PartialPaymentForm";
import { useTransactions } from "@/hooks/useTransactions";
import { useStockProducts } from "@/hooks/useStockProducts";
import { cn } from "@/lib/utils";

interface NotificationBellProps {
  variant?: 'light' | 'dark';
}

export function NotificationBell({ variant = 'dark' }: NotificationBellProps) {
  const { activeOrganizationId } = useActiveOrganization();
  const { schedules, loading, markAsNotified, createScheduleForRemainingBalance } = useNotificationSchedules();
  const { addPartialPayment, getTransactionById } = useTransactions();
  const { getLowStockProducts, isLoading: stockLoading } = useStockProducts();
  const [payingSchedule, setPayingSchedule] = useState<any>(null);
  const [open, setOpen] = useState(false);

  const currentDay = new Date().getDate();

  // Filter schedules for today
  const todaySchedules = schedules.filter(schedule => 
    schedule.notificationDay === currentDay &&
    schedule.isActive &&
    schedule.currentInstallment < schedule.installmentsCount
  );

  // Get low stock products
  const lowStockProducts = getLowStockProducts();

  const totalNotifications = todaySchedules.length + lowStockProducts.length;
  const hasNotifications = totalNotifications > 0;

  const handleWhatsApp = (schedule: any) => {
    const phone = schedule.patientPhone?.replace(/\D/g, '') || '';
    const currentInstallment = (schedule.currentInstallment || 0) + 1;
    const message = encodeURIComponent(
      `Olá ${schedule.patientName}!\n\n` +
      `Lembrete sobre sua parcela de ${formatCurrency(schedule.installmentValue)} referente a ${schedule.description || 'procedimento'}.\n` +
      `Parcela ${currentInstallment} de ${schedule.installmentsCount}.`
    );
    window.open(`https://wa.me/55${phone}?text=${message}`, '_blank');
  };

  const handleMarkNotified = (schedule: any) => {
    setPayingSchedule(schedule);
  };

  const handlePaymentSave = async (data: any) => {
    if (!payingSchedule) return;

    try {
      // Convert dd/MM/yyyy to YYYY-MM-DD
      const [day, month, year] = data.date.split('/');
      const formattedDate = `${year}-${month}-${day}`;

      // If schedule has linked transaction, use addPartialPayment to properly link and update
      if (payingSchedule.transactionId) {
        await addPartialPayment(
          payingSchedule.transactionId,
          data.value,
          formattedDate,
          data.paymentMethod
        );
      }

      // Mark as notified (advances installment counter)
      const result = await markAsNotified(payingSchedule);

      // After payment, check if there's remaining balance and schedule is complete
      if (result.success && result.isComplete && payingSchedule.transactionId) {
        // Fetch updated transaction to get current balance
        const updatedTransaction = await getTransactionById(payingSchedule.transactionId);
        
        if (updatedTransaction) {
          const originalAmount = updatedTransaction.original_amount || updatedTransaction.amount;
          const paidAmount = updatedTransaction.paid_amount || 0;
          const remainingBalance = originalAmount - paidAmount;

          // If there's still balance, create new notification for next month
          if (remainingBalance > 0) {
            await createScheduleForRemainingBalance(payingSchedule, remainingBalance);
          }
        }
      }

      setPayingSchedule(null);
    } catch (error) {
      console.error('Error processing payment:', error);
    }
  };

  if (!activeOrganizationId) return null;

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className={`relative ${variant === 'light' ? 'text-foreground hover:bg-muted' : 'text-sidebar-foreground hover:bg-white/10'}`}
        >
            <Bell className={cn("h-5 w-5", hasNotifications && "animate-pulse")} />
            {hasNotifications && (
              <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center font-medium">
                {totalNotifications}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="end">
          <div className="p-3 border-b bg-muted/50">
            <h3 className="font-semibold text-sm flex items-center gap-2">
              <Bell className="h-4 w-4" />
              Notificações
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {totalNotifications} pendente{totalNotifications !== 1 ? 's' : ''}
            </p>
          </div>
          
          <ScrollArea className="max-h-80">
            {loading || stockLoading ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                Carregando...
              </div>
            ) : !hasNotifications ? (
              <div className="p-6 text-center">
                <div className="text-3xl mb-2">🎉</div>
                <p className="text-sm text-muted-foreground">
                  Nenhuma notificação pendente
                </p>
              </div>
            ) : (
              <div>
                {/* Stock Alerts Section */}
                {lowStockProducts.length > 0 && (
                  <div>
                    <div className="p-2 bg-amber-50 dark:bg-amber-950/30 border-b flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
                        Estoque Baixo ({lowStockProducts.length})
                      </span>
                    </div>
                    <div className="divide-y">
                      {lowStockProducts.slice(0, 5).map((product) => {
                        const isOutOfStock = product.current_quantity <= 0;
                        return (
                          <div key={product.id} className="p-3 hover:bg-muted/50 transition-colors">
                            <div className="flex items-center gap-2">
                              <Package className={cn(
                                "h-4 w-4",
                                isOutOfStock ? "text-destructive" : "text-amber-500"
                              )} />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">{product.name}</p>
                                <p className={cn(
                                  "text-xs",
                                  isOutOfStock ? "text-destructive" : "text-amber-600"
                                )}>
                                  {isOutOfStock 
                                    ? "Esgotado!" 
                                    : `${product.current_quantity} unid. (mín: ${product.minimum_quantity})`
                                  }
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {lowStockProducts.length > 5 && (
                        <div className="p-2 text-center">
                          <Link to="/stock" onClick={() => setOpen(false)}>
                            <Button variant="ghost" size="sm" className="text-xs">
                              Ver todos ({lowStockProducts.length - 5} mais)
                            </Button>
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Charges Section */}
                {todaySchedules.length > 0 && (
                  <div>
                    <div className="p-2 bg-muted/50 border-y flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-primary" />
                      <span className="text-xs font-medium">
                        Cobranças de Hoje ({todaySchedules.length})
                      </span>
                    </div>
                    <div className="divide-y">
                      {todaySchedules.map((schedule) => {
                        const currentInstallment = (schedule.currentInstallment || 0) + 1;
                        return (
                          <div key={schedule.id} className="p-3 hover:bg-muted/50 transition-colors">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">
                                  {schedule.patientName}
                                </p>
                                <p className="text-xs text-muted-foreground truncate">
                                  {schedule.description || 'Cobrança agendada'}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-sm font-semibold text-primary">
                                    {formatCurrency(schedule.installmentValue)}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    Parcela {currentInstallment}/{schedule.installmentsCount}
                                  </span>
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-2 mt-2">
                              {schedule.patientPhone && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs flex-1"
                                  onClick={() => handleWhatsApp(schedule)}
                                >
                                  <MessageCircle className="h-3 w-3 mr-1" />
                                  WhatsApp
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="default"
                                className="h-7 text-xs flex-1"
                                onClick={() => handleMarkNotified(schedule)}
                              >
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Marcar Pago
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
          
          <div className="p-2 border-t bg-muted/30">
            <Link to="/finance">
              <Button 
                variant="ghost" 
                size="sm" 
                className="w-full text-xs"
                onClick={() => setOpen(false)}
              >
                Ver todas as cobranças
              </Button>
            </Link>
          </div>
        </PopoverContent>
      </Popover>

      {payingSchedule && (
        <PartialPaymentForm
          open={!!payingSchedule}
          onOpenChange={(open) => !open && setPayingSchedule(null)}
          transaction={{
            id: payingSchedule.transactionId || '',
            description: payingSchedule.description || payingSchedule.patientName,
            person: payingSchedule.patientName,
            valorOriginal: payingSchedule.totalAmount,
            valorPago: payingSchedule.installmentValue * (payingSchedule.currentInstallment || 0)
          }}
          suggestedValue={payingSchedule.installmentValue}
          onSave={handlePaymentSave}
        />
      )}
    </>
  );
}
